import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import groupBy from 'lodash/groupBy';

import State from 'Helpers/State';
import GeneralUtils from 'Helpers/GeneralUtils';
import { createOrder, giveawayOrder } from 'Redux/ducks/checkout';
import {
  toggleLoginModal,
  toggleGiveawayEmailRequiredModal,
  toggleVoucherEmailRequiredModal,
  toggleEpicGenbaModal,
  toggleSteamModal,
} from 'Redux/ducks/modal';
import {
  removeVoucher,
} from 'Redux/ducks/voucher';
import store, { history } from 'Redux/store';
import { refreshAuth } from 'Redux/ducks/auth';
import cartCalcSelector from 'Redux/reselects/cartcalc';
import { countryCode } from 'Helpers/Geo/data';


const CheckoutUtils = {};

CheckoutUtils.creatingOrder = false;
CheckoutUtils.authChecking = false;

CheckoutUtils.authComplete = function authComplete() {
  if (!this.creatingOrder && this.authChecking) {
    this.authChecking = false;
    this.createPayment();
  }
};

CheckoutUtils.billingComplete = function billingComplete() {
  if (this.waitingForBilling) {
    this.waitingForBilling = false;
    this.createPayment();
  }
};

CheckoutUtils.getGiveawayProducts = function getGiveawayProducts() {
  const cartNotCartcalc = State.getState('cart');
  const giveawayProducts = [];
  cartNotCartcalc.items.forEach((item) => {
    if (item.giveaway) {
      if (item.type === 'bundle' && item.tierIndex === 0) {
        giveawayProducts.push(item);
      }
      if (item.type !== 'bundle') {
        giveawayProducts.push(item);
      }
    }
  });
  return giveawayProducts;
};

CheckoutUtils.getGiveawayProductIds = function getGiveawayProductIds() {
  return CheckoutUtils.getGiveawayProducts().map(item => item._id);
};

CheckoutUtils.checkIfLoggedIn = async function checkIfLoggedIn(page) {
  const auth = State.getState('auth');
  if (auth && !auth.authenticated) {
    store.dispatch(toggleLoginModal(page));
    this.authChecking = true;
    return false;
  }
  await store.dispatch(refreshAuth());
  return true;
};

CheckoutUtils.checkForEpicAccount = async function checkForEpicAccount(items) {
  const auth = State.getState('auth');

  if (items.find(item => item.source === 'epicGenba')) {
    if (auth && auth.epic && auth.epic.id) {
      return true;
    }

    store.dispatch(toggleEpicGenbaModal());
    return false;
  }
  return true;
};

CheckoutUtils.checkForSteam = function checkForSteam() {
  const steamGiveawayProducts = this.getGiveawayProducts().filter(i => i.giveaway_steam);
  if (steamGiveawayProducts.length === 0) {
    return true;
  }
  const auth = State.getState('auth');
  if (!get(auth, 'steam.steam_id') || get(auth, 'steam.isLimitedAccount')) {
    store.dispatch(toggleSteamModal());
    return false;
  }
  return true;
};

CheckoutUtils.checkForNewsletter = function checkForNewsletter() {
  let auth = State.getState('auth');
  const newsletterGiveawayProducts = this.getGiveawayProducts().filter(i => i.giveaway_newsletter);
  if (newsletterGiveawayProducts.length > 0) {
    if (
      auth &&
      (auth.email_newsletter || auth.localNewsletterConsent)
    ) {
      return true;
    }
    store.dispatch(toggleGiveawayEmailRequiredModal());
    return false;
  }
  const voucher = State.getState('voucher');
  const newsletter = get(voucher, 'voucher.newsletter');
  if (newsletter &&
    !(auth.email_newsletter || auth.localNewsletterConsent)
  ) {
    store.dispatch(toggleVoucherEmailRequiredModal());
    auth = State.getState('auth');
    if (auth.email_newsletter || auth.localNewsletterConsent) {
      return true;
    }
    return false;
  }
  return true;
};

CheckoutUtils.cancelAndRemoveVoucher = async function cancelAndRemoveVoucher() {
  await store.dispatch(removeVoucher());
  store.dispatch(toggleVoucherEmailRequiredModal());
};

CheckoutUtils.goToPaymentPage = () => {
  history.push('/payment');
};

CheckoutUtils.doGiveawayPayment = (orderId, cart, giveawayProductIds) => {
  if (
    giveawayProductIds.length > 0 &&
    cart.items.length === giveawayProductIds.length
  ) {
    store.dispatch(giveawayOrder(orderId));
  }
};

CheckoutUtils.createPayment = async function createPayment() {
  this.creatingOrder = true;
  const state = State.getState();
  const cart = cartCalcSelector(state);
  const checkIfLoggedIn = await this.checkIfLoggedIn('checkout');
  if (!checkIfLoggedIn) {
    return false;
  }
  const checkForEpicAccount = await this.checkForEpicAccount(cart.items);
  if (!checkForEpicAccount) {
    return false;
  }

  this.creatingOrder = false;
  const checkForSteam = this.checkForSteam();
  if (!checkForSteam) {
    return false;
  }
  const checkForNewsletter = this.checkForNewsletter();
  if (!checkForNewsletter) {
    return false;
  }
  CheckoutUtils.continueToPayment();
  return true;
};

CheckoutUtils.continueToPayment = async function createPayment() {
  const lastSeenCoBranding = localStorage.getItem('lastSeenCoBranding');
  const today = new Date().getTime();
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  const state = State.getState();
  const cart = cartCalcSelector(state);
  const voucher = state.voucher;
  const actualVoucher = get(voucher, 'voucher', {});
  let voucherCode = get(actualVoucher, 'code', '');
  const voucherText = get(voucher, 'voucherText', '');
  if (actualVoucher.discount_type === 'Account' || actualVoucher.discount_type === 'Unique') voucherCode = voucherText;
  const { locale } = window;

  const orderOptions = {
    snowplowId: GeneralUtils.getSnowplowId(),
    ref_code: GeneralUtils.getReferalCode(),
    locale,
    giftOrder: State.getGiftOrderData(),
    cartcalcTotal: cart.total,
    localNewsletterConsent: state.auth.localNewsletterConsent,
  };
  const siteBrand = localStorage.getItem('siteBrand');
  if (siteBrand && lastSeenCoBranding && (today - Number(lastSeenCoBranding) < thirtyDays)) {
    orderOptions.siteBrand = siteBrand;
  }

  if (cart.total !== 0 || actualVoucher.type_of_discount === 'money') {
    orderOptions.code = voucherCode;
  }

  const giveawayProductIds = this.getGiveawayProductIds();
  const orderData = await store.dispatch(createOrder(orderOptions));

  if (orderData.type && orderData.type === 'billing') {
    this.waitingForBilling = true;
    store.dispatch({ type: 'frontend/checkout/BILLING_DETAILS_REQUESTED', payload: orderData.data.requiredFields });
    history.push('/billing');
    return true;
  }

  if (isEmpty(orderData.order) || !get(orderData, 'order._id', false)) {
    return false;
  }

  if (orderData.order.items.every(item => item.giveaway || get(item, 'report.free_product_id')) &&
    orderData.order.payment &&
    orderData.order.payment.total === 0) {
    return this.doGiveawayPayment(orderData.order._id, cart, giveawayProductIds);
  }

  const apiPaymentData = get(orderData, 'paymentData', {});

  if (apiPaymentData.billingFields) {
    store.dispatch({ type: 'frontend/checkout/BILLING_DETAILS_REQUESTED', payload: apiPaymentData.billingFields });
  }

  if (orderData.isAmountVoucher) {
    // Don't need stripe setup if user is paying fully with voucher
    // TODO use a separate flow for this, no point loading stripe
    return this.goToPaymentPage();
  }

  const orderTotal = get(orderData, 'order.payment.total', null);
  let cartcalcTotal = cart.total * 100;
  cartcalcTotal = Math.round(cartcalcTotal);

  if (cartcalcTotal !== orderTotal) {
    const payload = {
      hitType: 'unequalOrder',
      orderId: get(orderData, 'order._id', null),
      basketId: get(orderData, 'order.aid', null),
      userId: get(orderData, 'order.uid', null),
      cartTotal: cart.total,
      orderTotal: get(orderData, 'order.payment.total', null),
    };
    store.dispatch({ type: 'frontend/payment/ORDER_TOTAL_UNEQUAL_VERSUS_CARTCALC', payload });
  }

  if (apiPaymentData.stripeRedirectUrl) {
    return window.location.assign(apiPaymentData.stripeRedirectUrl);
  }

  if (!apiPaymentData.stripeClientSecret) {
    throw new Error('Missing stripeClientSecret');
  }

  return this.goToPaymentPage();
};

CheckoutUtils.openWindowWithPostData = (url, data) => {
  const form = document.createElement('form');
  form.target = '_self';
  form.method = 'POST';
  form.action = url;
  form.style.display = 'none';
  form.enctype = 'application/x-www-form-urlencoded';

  const keys = Object.keys(data);

  keys.forEach((key) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = data[key];
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

CheckoutUtils.openWindow = (url) => {
  window.open(url, '_self');
};

CheckoutUtils.toQuantityItems = (items) => {
  const returnItems = [];
  const groupedItemsObject = groupBy(items, '_id');
  items.forEach((item) => {
    const returnItem = Object.assign({}, item);
    returnItem.quantity = groupedItemsObject[item._id].length;
    returnItems.push(returnItem);
  });
  return returnItems;
};

CheckoutUtils.getCompleteOrderTransaction = (items) => {
  const returnObject = { transactionTax: 0, transactionProducts: [] };
  (items || []).forEach((item) => {
    const newObject = {
      sku: '',
      name: '',
      category: '',
      price: 0,
      quantity: 0,
      local_price: 0,
      supplier_ids: [],
    };
    if (item._id) {
      newObject.sku = item._id;
    }
    if (item.name) {
      newObject.name = item.name;
    }
    if (item.supplier_id) {
      newObject.supplier_ids.push(item.supplier_id);
    }
    if (item.bundles.length) {
      item.bundles.forEach((tiers) => {
        tiers.games.forEach((game) => {
          newObject.supplier_ids.push(game.supplier_id);
        });
      });
    }
    if (item.type) {
      newObject.category = item.type;
    }
    if (item.display_type) {
      newObject.display_type = item.display_type;
    }
    let price = 0;
    const quantity = 1;
    if (item.report) {
      if (item.report.total) {
        price = item.report.total;
      }
      if (item.report.vat) {
        returnObject.transactionTax += item.report.vat;
      }
    }
    let localPrice = 0;
    if (item.payment) {
      if (item.payment.total) {
        localPrice = item.payment.total;
      }
    }
    newObject.price = price;
    newObject.itemTax = item.report.vat;
    newObject.quantity = quantity;
    newObject.local_price = localPrice;
    newObject.discountPercent = get(item, 'discount.percent');
    newObject.fullPrice = get(item, 'report.pickAndMixOriginalPrice'); // unusual - only reference to full price - used in other places in the system
    returnObject.transactionProducts.push(newObject);
  });
  return returnObject;
};


CheckoutUtils.evaluateCartItems = (items) => {
  let showRegionAvailablility = true;

  items.forEach((item) => {
    const includedCodes = item.regions_included || [];
    const excludedCodes = item.regions_excluded || [];

    // isRegionExcluded is legacy moved into checkout util
    // https:// fandom.slack.com/archives/G01V4VBBV1U/p1670493878882539?name=G01V4VBBV1U&perma=1670493878882539
    // https:// github.com/BundleStars/frontend/commit/21d54306365af9e4cf526990c9453f7e0b5a7936

    if (Array.isArray(excludedCodes) && excludedCodes.includes(countryCode)) {
      showRegionAvailablility = false;
      item.isRegionExcluded = true; // eslint-disable-line no-param-reassign
    }

    if (Array.isArray(includedCodes)
        && includedCodes.length > 0 && !includedCodes.includes(countryCode)) {
      showRegionAvailablility = false;
      item.isRegionExcluded = true; // eslint-disable-line no-param-reassign
    }
  });

  return { showRegionAvailablility };
};

export default CheckoutUtils;
